import { Component, inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatCardModule } from '@angular/material/card';
import { MatTabsModule } from '@angular/material/tabs';
import { MatTableModule } from '@angular/material/table';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatMenuModule } from '@angular/material/menu';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { FormsModule } from '@angular/forms';
import { forkJoin } from 'rxjs';
import { finalize } from 'rxjs/operators';

import { SuperAdminService, PendingOrg, ArchivedOrg, DeletionStrategy } from '../services/super-admin.service';
import { LoaderService } from '../loader/loader.service';
import { SuccessMessageComponent } from '../shared/success-message/success-message.component';
import { ConfirmDialogComponent } from './confirm-dialog.component';
import { RejectDialogComponent } from './reject-dialog.component';
import { OrganizationDetailsDialogComponent } from './organization-details-dialog.component';
import { DeactivateDialogComponent } from './deactivate-dialog.component';
import { ReactivateDialogComponent } from './reactivate-dialog.component';
import { Subscription } from 'rxjs';
import { OnDestroy } from '@angular/core';

@Component({
  selector: 'app-organization-management',
  standalone: true,
  imports: [
    CommonModule,
    MatCardModule,
    MatTabsModule,
    MatTableModule,
    MatIconModule,
    MatButtonModule,
    MatMenuModule,
    MatSnackBarModule,
    MatTooltipModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    FormsModule
  ],
  templateUrl: './organization-management.component.html',
  styleUrls: ['./organization-management.component.scss']
})
export class OrganizationManagementComponent implements OnInit, OnDestroy {
  private service = inject(SuperAdminService);
  private snackBar = inject(MatSnackBar);
  private loaderService = inject(LoaderService);
  private dialog = inject(MatDialog);
  private subscriptions = new Subscription();

  // Data Sources
  pendingData: PendingOrg[] = [];
  approvedData: any[] = [];
  archivedData: ArchivedOrg[] = [];

  // Loading state
  isLoading = false;

  // Table Configurations
  pendingColumns: string[] = ['date', 'name', 'admin', 'contact', 'action'];
  approvedColumns: string[] = ['name', 'type', 'contact', 'status', 'action'];
  archivedColumns: string[] = ['name', 'type', 'reviewedAt', 'archivedBy', 'action'];

  ngOnInit() {
    this.loaderService.closeLoader();
    this.refreshData();

    const refreshSub = this.service.onDataRefreshNeeded.subscribe({
      next: () => {
        console.log('🔔 Received data refresh notification');
        this.refreshData();
      },
      error: (err) => {
        console.error('❌ Error in data refresh subscription:', err);
      }
    });

    this.subscriptions.add(refreshSub);
  }

  ngOnDestroy() {
    this.subscriptions.unsubscribe();
  }

  refreshData() {
    if (this.isLoading) {
      console.log('⏳ Refresh already in progress, skipping...');
      return;
    }

    console.log('🔄 Starting data refresh...');
    this.isLoading = true;

    forkJoin({
      pending: this.service.getPendingApprovals(),
      approved: this.service.getApprovedOrganizations(),
      archived: this.service.getArchivedOrganizations()
    }).pipe(
      finalize(() => {
        this.isLoading = false;
        console.log('✅ Data refresh completed');
      })
    ).subscribe({
      next: ({ pending, approved, archived }) => {
        this.pendingData = [...pending];

        this.approvedData = approved.map(item => {
          const fhir = item.rawFhir ?? item.RawFhir ?? item;

          const isParentOrg = fhir?.extension?.some((ext: any) => {
            const isParentExtension = ext.url === 'http://elikita.org/fhir/StructureDefinition/is-parent-organization';
            if (!isParentExtension) return false;

            let boolValue = false;
            if (ext.valueBoolean !== undefined) {
              boolValue = ext.valueBoolean === true || ext.valueBoolean?.value === true;
            } else if (ext.value !== undefined) {
              boolValue = ext.value === true || ext.value?.value === true;
            }
            return boolValue;
          }) ?? false;

          return {
            id: this.extractValue(fhir?.id),
            name: this.extractValue(fhir?.name) ?? '—',
            type: fhir?.type?.[0]?.text?.value ?? '—',
            contact:
              fhir?.telecom?.find((t: any) => t.system?.value === 'phone')?.value?.value
              ?? fhir?.telecom?.[0]?.value?.value
              ?? '—',
            address: fhir?.address?.[0]?.text?.value ?? '—',
            active: this.extractValue(fhir?.active) ?? true,
            raw: item,
            rawFhir: fhir,
            isParentOrg: isParentOrg,
            approvedAt: item.approval?.reviewedAt || this.extractValue(fhir?.meta?.lastUpdated) || null
          };
        }).sort((a, b) => {
          const dateA = a.approvedAt ? new Date(a.approvedAt).getTime() : 0;
          const dateB = b.approvedAt ? new Date(b.approvedAt).getTime() : 0;
          return dateB - dateA;
        });

        this.archivedData = [...archived];

        console.log('📊 Data loaded - Pending:', this.pendingData.length, 'Approved:', this.approvedData.length, 'Archived:', this.archivedData);
      },
      error: (err) => {
        console.error('❌ Error loading organization data', err);
        this.snackBar.open('Failed to load organization data.', 'Close', {
          duration: 5000,
          panelClass: ['snackbar-error']
        });
      }
    });
  }

  isParentOrganization(element: any): boolean {
    return element.isParentOrg === true;
  }

  viewPendingDetails(element: PendingOrg) {
    const dialogRef = this.dialog.open(OrganizationDetailsDialogComponent, {
      width: '700px',
      maxWidth: '90vw',
      maxHeight: '90vh',
      data: {
        organizationId: element.fhirResourceId,
        isPending: true,
        approvalId: element.id,
        organizationName: element.organizationName
      }
    });

    dialogRef.afterClosed().subscribe(result => {
  if (!result?.success) return;

  if (result.action === 'approve') {
    // 1️⃣ Remove from pending immediately
    this.pendingData = this.pendingData.filter(
      p => p.fhirResourceId !== result.organizationId
    );

    // 2️⃣ Optional: insert into approved list optimistically
    if (result.organizationData) {
      const fhir = result.organizationData;

      this.approvedData = [
        {
          id: fhir.id?.value ?? fhir.id,
          name: fhir.name?.value ?? fhir.name,
          type: fhir.type?.[0]?.text?.value ?? '—',
          contact: fhir.telecom?.[0]?.value?.value ?? '—',
          active: true,
          rawFhir: fhir,
          approvedAt: new Date()
        },
        ...this.approvedData
      ];
    }
  }

  if (result.action === 'reject') {
    // Remove from pending immediately
    this.pendingData = this.pendingData.filter(
      p => p.fhirResourceId !== result.organizationId
    );
  }


});
  }

  viewApprovedDetails(element: any) {
    const dialogRef = this.dialog.open(OrganizationDetailsDialogComponent, {
      width: '700px',
      maxWidth: '90vw',
      maxHeight: '90vh',
      data: {
        organizationId: element.rawFhir?.id?.value || element.id,
        isPending: false,
        approvalData: element.approval || element.raw?.approval
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result && result.shouldRefresh) {
        this.refreshData();
      }
    });
  }

  viewArchivedDetails(element: ArchivedOrg) {
    const dialogRef = this.dialog.open(OrganizationDetailsDialogComponent, {
      width: '700px',
      maxWidth: '90vw',
      maxHeight: '90vh',
      data: {
        organizationId: element.fhirResourceId,
        isPending: false,
        isArchived: true,
        approvalData: element.rawFhir?.approval || (element as any).approval
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result && result.shouldRefresh) {
        this.refreshData();
      }
    });
  }

  onApprove(element: PendingOrg) {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      width: '400px',
      data: {
        title: 'Approve Organization',
        message: `Are you sure you want to approve <strong>${element.organizationName}</strong>? This will:`,
        details: [
          'Activate the organization in the system',
          'Create an admin account for ' + element.adminFirstName + ' ' + element.adminLastName,
          'Send login credentials to ' + element.adminEmail
        ],
        confirmText: 'Approve',
        cancelText: 'Cancel',
        confirmColor: 'primary'
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.performApproval(element);
      }
    });
  }

  private performApproval(element: PendingOrg) {
    console.log('🚀 Performing approval for:', element.organizationName);

    this.service.approveOrganization(element.id).subscribe({
      next: (response) => {
        console.log('✅ Approval successful:', response);

        // Remove from pending list immediately
        this.pendingData = this.pendingData.filter(p => p.id !== element.id);

        this.snackBar.openFromComponent(SuccessMessageComponent, {
          data: { message: `${element.organizationName} Approved Successfully!` },
          duration: 3500,
          verticalPosition: 'top'
        });

        // Schedule a full refresh to get the newly approved org from backend
        setTimeout(() => this.refreshData(), 1500);
      },
      error: (err: any) => {
        console.error('❌ Approval error:', err);
        this.snackBar.open('Approval Failed. Please check server logs.', 'Close', {
          duration: 5000,
          panelClass: ['snackbar-error']
        });
      }
    });
  }

  onReject(element: PendingOrg) {
    const dialogRef = this.dialog.open(RejectDialogComponent, {
      width: '450px',
      data: {
        organizationName: element.organizationName,
        adminName: element.adminFirstName + ' ' + element.adminLastName
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result && result.reason) {
        this.performRejection(element, result.reason);
      }
    });
  }

  private performRejection(element: PendingOrg, reason: string) {
    console.log('🚀 Performing rejection for:', element.organizationName);

    this.service.rejectOrganization(element.id, reason).subscribe({
      next: (response) => {
        console.log('✅ Rejection successful:', response);

        // Remove from pending list immediately
        this.pendingData = this.pendingData.filter(p => p.id !== element.id);

        this.snackBar.open('Organization Registration Rejected', 'OK', { duration: 3000 });
      },
      error: (err: any) => {
        console.error('❌ Rejection error:', err);
        this.snackBar.open('Operation failed', 'Close', { duration: 3000 });
      }
    });
  }

  onDeactivate(element: any) {
    const dialogRef = this.dialog.open(DeactivateDialogComponent, {
      width: '600px',
      data: {
        organizationName: element.name,
        organizationId: element.id,
        hasData: true
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.performDeactivation(element, result.strategy, result.reason);
      }
    });
  }

  private performDeactivation(element: any, strategy: DeletionStrategy, reason?: string) {
    console.log('🚀 Performing deactivation for:', element.name, 'Strategy:', strategy);

    this.service.deactivateOrganization(element.id, reason).subscribe({
      next: (response) => {
        console.log('✅ Deactivation successful:', response);

        // Remove from approved list immediately
        this.approvedData = this.approvedData.filter(org => org.id !== element.id);

        // Add to archived list
        const archivedOrg: ArchivedOrg = {
          id: element.raw?.id || element.id,
          organizationName: element.name,
          fhirResourceId: element.id,
          status: 2, // Deactivated status
          reviewedAt: new Date().toISOString(),
          reviewedBy: 'Current User',
          rawFhir: element.rawFhir
        };
        this.archivedData = [archivedOrg, ...this.archivedData];

        let message = '';
        switch (strategy) {
          case DeletionStrategy.SoftDelete:
            message = `${element.name} has been deactivated successfully.`;
            break;
          case DeletionStrategy.Archive:
            message = `${element.name} has been archived successfully.`;
            break;
          case DeletionStrategy.HardDelete:
            message = `${element.name} has been permanently deleted.`;
            break;
        }

        this.snackBar.openFromComponent(SuccessMessageComponent, {
          data: { message },
          duration: 3500,
          verticalPosition: 'top'
        });
      },
      error: (err: any) => {
        console.error('❌ Deactivation failed:', err);
        this.snackBar.open('Failed to deactivate organization. Please try again.', 'Close', {
          duration: 5000,
          panelClass: ['snackbar-error']
        });
      }
    });
  }

  /**
   * Handle permanent deletion of organization
   */
  onPermanentDelete(element: any) {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      width: '500px',
      data: {
        title: '⚠️ Permanent Deletion',
        message: `Are you sure you want to PERMANENTLY delete <strong>${element.name || element.organizationName}</strong>?`,
        details: [
          'This action CANNOT be undone',
          'All organization data will be permanently removed',
          'This should only be done for organizations with NO patient data'
        ],
        confirmText: 'Delete Permanently',
        cancelText: 'Cancel',
        confirmColor: 'warn'
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.performPermanentDelete(element);
      }
    });
  }

  private performPermanentDelete(element: any) {
    const orgId = element.fhirResourceId || element.id;
    const orgName = element.name || element.organizationName || this.extractValue(element.rawFhir?.name);

    console.log('🚀 Performing permanent deletion for:', orgName);

    this.service.deleteOrganizationPermanently(orgId).subscribe({
      next: (response) => {
        console.log('✅ Permanent deletion successful:', response);

        // Remove from ALL lists immediately
        this.approvedData = this.approvedData.filter(org => org.id !== orgId);
        this.archivedData = this.archivedData.filter(org =>
          (org.fhirResourceId || org.id) !== orgId
        );
        this.pendingData = this.pendingData.filter(org =>
          (org.fhirResourceId || org.id) !== orgId
        );

        this.snackBar.openFromComponent(SuccessMessageComponent, {
          data: { message: `${orgName} has been permanently deleted.` },
          duration: 3500,
          verticalPosition: 'top'
        });
      },
      error: (err: any) => {
        console.error('❌ Permanent deletion failed:', err);

        let errorMessage = 'Failed to delete organization. ';
        if (err.error?.message) {
          errorMessage += err.error.message;
        } else if (err.status === 409) {
          errorMessage += 'Organization may have associated data.';
        } else {
          errorMessage += 'Please try again.';
        }

        this.snackBar.open(errorMessage, 'Close', {
          duration: 5000,
          panelClass: ['snackbar-error']
        });
      }
    });
  }

  onReactivate(element: any) {
    const dialogRef = this.dialog.open(ReactivateDialogComponent, {
      width: '500px',
      data: {
        organizationName: element.organizationName || this.extractValue(element.rawFhir?.name)
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.performReactivation(element);
      }
    });
  }

  private performReactivation(element: any) {
    const orgId = element.fhirResourceId || element.id;
    const orgName = element.organizationName || this.extractValue(element.rawFhir?.name);

    console.log('🚀 Performing reactivation for:', orgName);

    this.service.reactivateOrganization(orgId).subscribe({
      next: (response) => {
        console.log('✅ Reactivation successful:', response);

        // Remove from archived list immediately
        this.archivedData = this.archivedData.filter(org =>
          (org.fhirResourceId || org.id) !== orgId
        );

        this.snackBar.openFromComponent(SuccessMessageComponent, {
          data: { message: `${orgName} has been reactivated successfully!` },
          duration: 3500,
          verticalPosition: 'top'
        });

        // Schedule a full refresh to get the reactivated org from backend
        setTimeout(() => this.refreshData(), 1500);
      },
      error: (err: any) => {
        console.error('❌ Reactivation failed:', err);
        this.snackBar.open('Failed to reactivate organization. Please try again.', 'Close', {
          duration: 5000,
          panelClass: ['snackbar-error']
        });
      }
    });
  }

  isActive(element: any): boolean {
    return element.active !== false;
  }

  private extractValue(obj: any): any {
    return obj?.value !== undefined ? obj.value : obj;
  }

  getType(org: any): string {
    if (org.type && org.type.length > 0) {
      if (org.type[0].text) return this.extractValue(org.type[0].text);
      if (org.type[0].coding?.[0]?.display) return this.extractValue(org.type[0].coding[0].display);
      if (org.type[0].coding?.[0]?.code) return this.extractValue(org.type[0].coding[0].code);
    }
    return 'Healthcare Provider';
  }

  getContact(org: any): string {
    if (!org.telecom || !Array.isArray(org.telecom)) return 'N/A';
    const phone = org.telecom.find((t: any) => this.extractValue(t.system) === 'phone');
    const contact = phone || org.telecom[0];
    return contact ? this.extractValue(contact.value) : 'N/A';
  }

  getAddress(org: any): string {
    if (!org.address || org.address.length === 0) return 'N/A';
    const a = org.address[0];
    let line = '';
    if (a.line && Array.isArray(a.line)) {
      line = a.line.map((l: any) => this.extractValue(l)).join(' ');
    }
    const city = this.extractValue(a.city);
    const state = this.extractValue(a.state);
    const parts = [line, city, state].filter(p => p && String(p).trim().length > 0);
    return parts.length > 0 ? parts.join(', ') : 'N/A';
  }

  getName(org: any): string {
    return this.extractValue(org.name) || 'Unknown Organization';
  }
}
